import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Ticket, Hotel, Utensils, Waves, Clock, ChevronRight, Sparkles, BadgePercent, Gift } from 'lucide-react';

const promotions = [
  {
    id: 1,
    tag: 'Más Popular',
    tagColor: 'bg-amber-400 text-amber-900',
    icon: Ticket,
    iconGradient: 'from-amber-400 to-orange-500',
    glow: 'shadow-orange-200',
    title: 'Ticket Mágico 4 Días · 4 Parques',
    description: 'Visita un parque por día: Magic Kingdom, EPCOT, Hollywood Studios y Animal Kingdom.',
    highlights: [
      'Solo $109 USD por día',
      'Válido hasta septiembre 2026',
      '4 parques incluidos',
    ],
    price: 'Desde $436 USD',
    priceNote: '$109 / día por persona',
    cta: 'Cotizar ahora',
    accent: 'from-amber-50 to-orange-50',
    border: 'border-orange-200',
    priceColor: 'text-orange-500',
  },
  {
    id: 2,
    tag: 'Familias con niños',
    tagColor: 'bg-pink-400 text-white',
    icon: Utensils,
    iconGradient: 'from-pink-500 to-rose-500',
    glow: 'shadow-pink-200',
    title: 'Plan de Comidas Gratis para Niños',
    description: 'Compra paquete hotel + boletos con plan de comidas para adultos y los niños comen gratis.',
    highlights: [
      'Niños de 3 a 9 años incluidos',
      'Durante toda la estancia 2026',
      'En hoteles Disney seleccionados',
    ],
    price: '¡Niños comen GRATIS!',
    priceNote: 'Al comprar paquete completo',
    cta: 'Ver paquetes',
    accent: 'from-pink-50 to-rose-50',
    border: 'border-pink-200',
    priceColor: 'text-pink-500',
  },
  {
    id: 3,
    tag: 'Exclusivo huéspedes',
    tagColor: 'bg-cyan-400 text-cyan-900',
    icon: Waves,
    iconGradient: 'from-cyan-400 to-blue-500',
    glow: 'shadow-cyan-200',
    title: 'Entrada Gratis el Día de Check-in',
    description: 'Hospédate en un hotel de la Disney Resorts Collection y accede gratis a un parque acuático el día de tu llegada.',
    highlights: [
      'Parque acuático incluido',
      'Solo el día de llegada',
      'Disney Resorts Collection',
    ],
    price: 'Parque acuático GRATIS',
    priceNote: 'El día de tu check-in',
    cta: 'Ver hoteles',
    accent: 'from-cyan-50 to-blue-50',
    border: 'border-cyan-200',
    priceColor: 'text-cyan-500',
  },
];

const PromoCard = ({ promo, index }) => {
  const Icon = promo.icon;
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      className={`relative bg-gradient-to-br ${promo.accent} rounded-3xl border ${promo.border} overflow-hidden flex flex-col`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ y: -8 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{ boxShadow: hovered ? `0 20px 40px -8px var(--tw-shadow-color)` : undefined }}
    >
      {/* Tag */}
      <div className="absolute top-4 right-4">
        <span className={`${promo.tagColor} text-xs font-bold px-3 py-1 rounded-full`}>
          {promo.tag}
        </span>
      </div>

      <div className="p-7 flex flex-col flex-1">
        {/* Icono */}
        <div className={`w-14 h-14 bg-gradient-to-br ${promo.iconGradient} rounded-2xl flex items-center justify-center mb-5 shadow-lg`}>
          <Icon className="w-7 h-7 text-white" />
        </div>

        {/* Título y descripción */}
        <h3 className="text-lg font-extrabold text-gray-800 mb-2 leading-tight pr-8">{promo.title}</h3>
        <p className="text-gray-500 text-sm leading-relaxed mb-5">{promo.description}</p>

        {/* Highlights */}
        <ul className="space-y-2 mb-6 flex-1">
          {promo.highlights.map((h, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
              <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-br ${promo.iconGradient} flex-shrink-0`} />
              {h}
            </li>
          ))}
        </ul>

        {/* Precio */}
        <div className="border-t border-gray-200/70 pt-4 mb-5">
          <p className={`text-xl font-extrabold ${promo.priceColor}`}>{promo.price}</p>
          <p className="text-gray-400 text-xs mt-0.5">{promo.priceNote}</p>
        </div>

        {/* CTA */}
        <a
          href="#quote"
          className={`flex items-center justify-between bg-gradient-to-r ${promo.iconGradient} text-white px-5 py-3 rounded-xl font-semibold text-sm shadow-md hover:opacity-90 transition-opacity duration-200 group`}
        >
          {promo.cta}
          <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
        </a>
      </div>
    </motion.div>
  );
};

const Promotions = () => {
  return (
    <section id="promotions" className="py-20 bg-white relative overflow-hidden">
      {/* Fondo decorativo */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(236,72,153,0.07),transparent)]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

        {/* Header */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-pink-50 border border-pink-100 text-pink-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            <Sparkles className="w-4 h-4" />
            Ofertas vigentes 2026
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
            Promociones Especiales
          </h2>
          <p className="mt-3 text-gray-500 text-lg max-w-xl mx-auto">
            Aprovecha las mejores ofertas de Disney para este año. Cupos limitados.
          </p>
        </motion.div>

        {/* Banner de urgencia */}
        <motion.div
          className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3 mb-10 max-w-2xl mx-auto justify-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <p className="text-amber-700 text-sm font-medium">
            El Ticket 4 Días es válido para fechas de inicio <strong>hasta septiembre 2026</strong>. ¡No esperes más!
          </p>
        </motion.div>

        {/* Grid de tarjetas — centradas con flex wrap */}
        <div className="flex flex-wrap justify-center gap-6">
          {promotions.map((promo, index) => (
            <div key={promo.id} className="w-full sm:w-72 flex">
              <PromoCard promo={promo} index={index} />
            </div>
          ))}
        </div>

        {/* CTA final */}
        <motion.div
          className="text-center mt-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          {/* Disclaimer */}
          <p className="text-gray-400 text-xs max-w-lg mx-auto mb-6 leading-relaxed">
            * Las promociones están sujetas a disponibilidad. Verifica fechas de validez antes de reservar. Precios y condiciones pueden cambiar sin previo aviso.
          </p>
          <p className="text-gray-400 text-sm mb-4">¿No encuentras lo que buscas? Armamos paquetes personalizados.</p>
          <a
            href="#quote"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-10 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            <Gift className="w-5 h-5" />
            Cotiza tu paquete ideal
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Promotions;